import { storage } from "@wlink/commons/core";
import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";
// @ts-ignore
import { usePermissionStore } from "@wlink/commons/config/stores/permission";
import request from "@wlink/request";

// 根据实际接口定义User类型
export interface User {
  id: number;
  name: string;
  full_name: string;
  user_type: string;
  avatar?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type UserRole = string;

export const useAuthStore = defineStore("auth", () => {
  const user = shallowRef<User | null>(null);
  const token = ref<string | null>(null);
  const isAuthenticated = ref(false);
  const isAuthReady = ref(false);

  // 初始化认证状态 - 从localStorage恢复状态
  const initAuth = async () => {
    const savedToken = storage.get("token");
    const savedUser = storage.get("user");
    if (savedToken && savedUser) {
      // 为恢复的用户数据也设置默认值
      const processedUserData: User = {
        ...savedUser,
        // 设置默认昵称：如果没有name，使用默认值
        name: savedUser.full_name || `用户${savedUser.id}`,
        // 设置默认头像：使用网络图片，基于用户ID生成
        avatar:
          savedUser.avatar ||
          "https://api.dicebear.com/7.x/avataaars/svg?seed=" +
            encodeURIComponent(savedUser.id?.toString() || "default"),
        // 确保更新时间存在
        updatedAt: savedUser.updatedAt || new Date().toISOString(),
      };

      token.value = savedToken;
      user.value = processedUserData;
      isAuthenticated.value = true;
      // 如果应用了默认值，更新localStorage
      if (!savedUser.full_name || !savedUser.avatar) {
        storage.set("user", processedUserData);
      }

      // 初始化权限
      try {
        // const permissionStore = usePermissionStore();
        // await permissionStore.initPermissions();
      } catch (error) {}
    }
    isAuthReady.value = true; // 初始化完成
  };

  // 设置认证信息 - 用于登录成功后设置状态
  const setAuth = async (authToken: string, userData: User) => {
    // 为用户数据设置默认值
    const processedUserData: User = {
      ...userData,
      // 设置默认昵称：如果没有name，使用默认值
      name: userData.full_name || `用户${userData.id}`,
      // 设置默认头像：使用网络图片，基于用户ID生成
      avatar:
        userData.avatar ||
        "https://api.dicebear.com/7.x/avataaars/svg?seed=" +
          encodeURIComponent(userData.id?.toString() || "default"),
      // 确保更新时间存在
      updatedAt: userData.updatedAt || new Date().toISOString(),
    };
    token.value = authToken;
    user.value = processedUserData;
    isAuthenticated.value = true;
    // 保存到本地存储
    storage.set("token", authToken);
    storage.set("user", processedUserData);

    // 初始化权限
    try {
      const permissionStore = usePermissionStore();
      await permissionStore.initPermissions();
    } catch (error) {}
  };

  // 登出
  const logout = async () => {
    try {
      // 调用登出接口
      await request("AuthAccountLogout", {});
    } catch (error) {
      // 即使接口调用失败，也要清除本地状态
    } finally {
      // 清除本地状态
      user.value = null;
      token.value = null;
      isAuthenticated.value = false;
      // 清除本地存储
      storage.remove("token");
      storage.remove("user");
      storage.remove("auditStatus");

      // 清除权限数据
      const permissionStore = usePermissionStore();
      permissionStore.$reset();

      // 跳转到登录页
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
    }
  };

  // 更新用户信息
  const updateUser = (userData: Partial<User>) => {
    if (user.value) {
      const updatedUser = {
        ...user.value,
        ...userData,
        updatedAt: new Date().toISOString(),
      };
      user.value = updatedUser;
      storage.set("user", updatedUser);
    }
  };

  // 检查token是否有效（可以扩展为调用后端验证）
  const validateToken = async () => {
    if (!token.value) {
      return false;
    }

    try {
      // 这里可以调用后端API验证token
      // 暂时返回true，后续可以实现真实的token验证
      return true;
    } catch (error) {
      return false;
    }
  };

  // 清除认证状态（用于token失效时）
  const clearAuth = () => {
    user.value = null;
    token.value = null;
    isAuthenticated.value = false;
    storage.remove("token");
    storage.remove("user");
    storage.remove("auditStatus");

    // 跳转到登录页（可选：某些场景下可能不需要自动跳转）
    // if (typeof window !== 'undefined') {
    //   window.location.href = '/login';
    // }
  };

  return {
    // 状态
    user,
    token,
    isAuthenticated,
    isAuthReady,

    // 方法
    initAuth,
    setAuth,
    logout,
    updateUser,
    validateToken,
    clearAuth,
  };
});
